import React, { useEffect, useMemo } from "react";
import { Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import { Download, FileDownloadOutlined } from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppTable from "components/Shared/AppTable";
import AppTableDateTime from "components/Shared/AppTableDateTime";
import HighlightedText from "components/Shared/HighlightedText";
import {
  useAppMeta,
  useAuth,
  useTablePagination,
} from "hooks";
import { useCustomParams } from "hooks/useCustomParams";
import {
  useLazyListEBillReportQuery,
} from "store/services/reportApi";
import {
  usePrintShipmentPriceCreditNoteMutation,
  usePrintShipmentPriceInvoiceMutation,
} from "store/services/shipmentApi";
import { usePrintCodTransferEBillMutation } from "store/services/PaymentsApi/codTransferApi";
import { usePrintShipmentRefundCreditNoteMutation } from "store/services/PaymentsApi/shipmentRefundApi";
import { exportExcelFile } from "utils/exportExcelFile";
import EBillReportFilter from "./EBillReportFilter";

const EXPORT_LIMIT = 10000;

const EBillReportListScreen = () => {
  const { viewEntity } = useAuth();
  const [searchParams] = useSearchParams();
  const { params } = useCustomParams();
  const { currentPage, perPage, handlePageChange, handlePerRowsChange } =
    useTablePagination();
  const [fetchReport, { data, isLoading, isFetching, error }] =
    useLazyListEBillReportQuery();
  const [printShipmentInvoice] = usePrintShipmentPriceInvoiceMutation();
  const [printShipmentCreditNote] = usePrintShipmentPriceCreditNoteMutation();
  const [printCodInvoice] = usePrintCodTransferEBillMutation();
  const [printRefundCreditNote] = usePrintShipmentRefundCreditNoteMutation();
  const keyword = searchParams.get("search") || "";

  const reportParams = useMemo(
    () => ({
      account: viewEntity?.entity?.account?._id,
      entity: viewEntity?.entity?._id,
      keyword,
      fromDate: params.fromDate || "",
      toDate: params.toDate || "",
    }),
    [
      viewEntity?.entity?.account?._id,
      viewEntity?.entity?._id,
      keyword,
      params.fromDate,
      params.toDate,
    ],
  );

  useEffect(() => {
    if (!params.timeRange || !viewEntity?.entity?._id) return;
    fetchReport({
      ...reportParams,
      page: currentPage,
      limit: perPage,
    });
  }, [
    currentPage,
    perPage,
    keyword,
    params.timeRange,
    params.fromDate,
    params.toDate,
    viewEntity,
    fetchReport,
    reportParams,
  ]);

  const handleDownload = async () => {
    const response = await fetchReport({
      ...reportParams,
      page: 1,
      limit: EXPORT_LIMIT,
    }).unwrap();

    exportExcelFile({
      sheetData: (response?.data || []).map((row) => ({
        "EBill Type": row.sourceType,
        "Document Type": row.documentType,
        "Invoice / Credit Note No": row.invoiceNo || "N/A",
        "EBill ID": row.eBillId || "N/A",
        "Shipment ID": row.shipmentId || "N/A",
        Reference: row.reference || "N/A",
        Amount: Number(row.amount || 0).toFixed(2),
        "Created At": row.createdAt
          ? new Date(row.createdAt).toLocaleString()
          : "N/A",
      })),
      fileName: `EBill_Report_${new Date().toISOString().slice(0, 10)}`,
    });
  };

  const handleBillDownload = async (row) => {
    try {
      let response;
      if (row.sourceType === "Shipment Price Invoice") {
        response = await printShipmentInvoice(row.sourceId).unwrap();
      } else if (row.sourceType === "Shipment Price Credit Note") {
        response = await printShipmentCreditNote({
          priceId: row.sourceId,
          creditNoteId: row.eBillId,
        }).unwrap();
      } else if (row.sourceType === "COD Transfer Invoice") {
        response = await printCodInvoice(row.sourceId).unwrap();
      } else if (row.sourceType === "Shipment Refund Credit Note") {
        response = await printRefundCreditNote({
          creditNoteId: row.eBillId || row.sourceId,
        }).unwrap();
      }

      const printUrl =
        response?.eBillPrintUrl ||
        response?.creditNotePrintUrl ||
        response?.data?.print_url ||
        response?.data?.printUrl ||
        response?.data?.url ||
        (typeof response?.data === "string" ? response.data : null);
      if (!printUrl) throw new Error("No EBill download URL returned");
      window.open(printUrl, "_blank", "noopener,noreferrer");
    } catch (downloadError) {
      toast.error(
        downloadError?.data?.message ||
          downloadError?.message ||
          "EBill download failed",
      );
    }
  };

  const columns = [
    {
      name: "EBill Type",
      selector: (row) => row.sourceType,
      cell: (row) => (
        <Typography variant="body2">{row.sourceType || "N/A"}</Typography>
      ),
      grow: 1.5,
    },
    {
      name: "Invoice / Credit Note No",
      selector: (row) => row.invoiceNo,
      cell: (row) => (
        <HighlightedText
          text={row.invoiceNo || "N/A"}
          highlight={keyword}
        />
      ),
      grow: 1.4,
    },
    {
      name: "Shipment ID",
      selector: (row) => row.shipmentId,
      cell: (row) => (
        <HighlightedText
          text={row.shipmentId || "N/A"}
          highlight={keyword}
        />
      ),
      grow: 1.3,
    },
    {
      name: "Reference",
      selector: (row) => row.reference,
      cell: (row) => row.reference || "N/A",
    },
    {
      name: "Amount",
      selector: (row) => row.amount,
      cell: (row) => `Rs ${(Number(row.amount) || 0).toFixed(2)}`,
    },
    {
      name: "Created At",
      selector: (row) => row.createdAt,
      cell: (row) => <AppTableDateTime date={row.createdAt} />,
    },
    {
      name: "Download",
      cell: (row) => (
        <Tooltip title="Download EBill">
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleBillDownload(row)}
            aria-label="Download EBill"
          >
            <FileDownloadOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
      ignoreRowClick: true,
      right: true,
    },
  ];

  useAppMeta("EBill Report");

  return (
    <BreadcrumbLayout
      breadcrumbs={[
        { title: "Reports", path: "/app/crm/reports/list" },
        { title: "EBill Report" },
      ]}
    >
      <BreadcrumbLayout.Error error={error} />
      <BreadcrumbLayout.Paper>
        <EBillReportFilter disabled={isLoading || isFetching} />
        <Box sx={{ display: "flex", justifyContent: "flex-end", p: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Download />}
            onClick={handleDownload}
            disabled={isLoading || isFetching}
          >
            Download Excel
          </Button>
        </Box>
        <AppTable
          columns={columns}
          data={data?.data || []}
          progressPending={isLoading || isFetching}
          paginationServer
          paginationPerPage={perPage}
          paginationDefaultPage={currentPage}
          paginationTotalRows={data?.totalItems || 0}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          noDataComponent={
            <Box sx={{ p: 4 }}>
              <Typography>No EBill invoices or credit notes found</Typography>
            </Box>
          }
        />
      </BreadcrumbLayout.Paper>
    </BreadcrumbLayout>
  );
};

export default EBillReportListScreen;
